import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Lock, KeyRound, Save, Loader2, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const ROLE_LABELS = { administrator: 'Administrator', supervisor: 'Supervisor', scheduler: 'Scheduler', staff: 'Staff', employee: 'Staff' };
const ROLE_STYLES = {
  administrator: 'bg-[#2D1B4E] text-white',
  supervisor: 'bg-[#6B3FA0] text-white',
  scheduler: 'bg-[#9B7ED8] text-white',
  staff: 'bg-[#F0EDF6] text-[#5A4D6E]',
  employee: 'bg-[#F0EDF6] text-[#5A4D6E]',
};

export default function ProfileTab({
  currentUser, profileForm, setProfileForm, profileSaving, handleSaveProfile,
  pwForm, setPwForm, pwSaving, handleChangePassword,
  showCurrent, setShowCurrent, showNew, setShowNew, pwStrength,
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card className="border-[#DED8EB] shadow-sm" data-testid="profile-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <User className="h-4 w-4 text-[#6B3FA0]" /> Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6B3FA0] to-[#9B7ED8] flex items-center justify-center text-white text-2xl font-bold shadow-md">
              {currentUser?.name?.charAt(0) || 'U'}
            </div>
            <div>
              <p className="font-bold text-[#2D1B4E] text-lg">{currentUser?.name}</p>
              <Badge className={`${ROLE_STYLES[currentUser?.role]} text-xs`}>{ROLE_LABELS[currentUser?.role]}</Badge>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[#2D1B4E] font-medium text-sm">Full Name</Label>
              <Input value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                className="border-[#DED8EB] focus:border-[#9B7ED8]" data-testid="profile-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#2D1B4E] font-medium text-sm">Email</Label>
              <Input value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                className="border-[#DED8EB] focus:border-[#9B7ED8]" type="email" data-testid="profile-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#2D1B4E] font-medium text-sm">Phone</Label>
              <Input value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                className="border-[#DED8EB] focus:border-[#9B7ED8]" placeholder="(555) 000-0000" data-testid="profile-phone" />
            </div>
          </div>
          <Button onClick={handleSaveProfile} disabled={profileSaving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2 w-full" data-testid="save-profile-btn">
            {profileSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {profileSaving ? 'Saving...' : 'Save Profile'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] shadow-sm" data-testid="password-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Lock className="h-4 w-4 text-[#6B3FA0]" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[#2D1B4E] font-medium text-sm">Current Password</Label>
            <div className="relative">
              <Input type={showCurrent ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                className="border-[#DED8EB] pr-10" data-testid="current-password" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E85A0] hover:text-[#6B3FA0]">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#2D1B4E] font-medium text-sm">New Password</Label>
            <div className="relative">
              <Input type={showNew ? 'text' : 'password'} value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                className="border-[#DED8EB] pr-10" data-testid="new-password" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E85A0] hover:text-[#6B3FA0]">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pwStrength && (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex gap-1 flex-1">
                  <div className={`h-1.5 rounded-full flex-1 transition-all ${pwStrength === 'weak' ? 'bg-red-400' : pwStrength === 'good' ? 'bg-[#FFB74D]' : 'bg-green-500'}`} />
                  <div className={`h-1.5 rounded-full flex-1 transition-all ${pwStrength === 'good' ? 'bg-[#FFB74D]' : pwStrength === 'strong' ? 'bg-green-500' : 'bg-gray-200'}`} />
                  <div className={`h-1.5 rounded-full flex-1 transition-all ${pwStrength === 'strong' ? 'bg-green-500' : 'bg-gray-200'}`} />
                </div>
                <span className={`text-xs font-medium ${pwStrength === 'weak' ? 'text-red-500' : pwStrength === 'good' ? 'text-[#FFB74D]' : 'text-green-600'}`}>
                  {pwStrength === 'weak' ? 'Weak' : pwStrength === 'good' ? 'Good' : 'Strong'}
                </span>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#2D1B4E] font-medium text-sm">Confirm New Password</Label>
            <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
              className={`border-[#DED8EB] ${pwForm.confirm && pwForm.confirm !== pwForm.newPw ? 'border-red-300 focus:border-red-400' : ''}`} data-testid="confirm-password" />
            {pwForm.confirm && pwForm.confirm !== pwForm.newPw && (
              <p className="text-xs text-red-500 flex items-center gap-1"><XCircle className="h-3 w-3" /> Passwords don't match</p>
            )}
            {pwForm.confirm && pwForm.confirm === pwForm.newPw && pwForm.newPw.length >= 8 && (
              <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Passwords match</p>
            )}
          </div>
          <Button onClick={handleChangePassword} disabled={pwSaving || !pwForm.current || !pwForm.newPw || pwForm.newPw !== pwForm.confirm || pwForm.newPw.length < 8}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2 w-full" data-testid="change-password-btn">
            {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {pwSaving ? 'Changing...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
